import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserSupportSenderRole,
  type UserSupportConversationDTO,
  type UserSupportMessageDTO,
} from '@klaro7/shared-types';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { resolveUploadUrl } from '@/lib/uploads';
import { MessageBubbleList, type BubbleMessage } from '@/features/messaging/MessageBubbleList';
import { MessageComposer } from '@/features/messaging/MessageComposer';
import { ImageLightbox } from '@/features/messaging/ImageLightbox';
import {
  useInbox, useSupportMessages, useSendSupportMessage, useMarkSupportRead,
  useSupportStatus, useSupportAssign, useOpenThread,
} from '../hooks/useSupportUsers';
import { useSupportUsersSocket } from '../hooks/useSupportUsersSocket';
import { SupportUserAttachment } from './SupportUserAttachment';
import { NewSupportUserThreadModal } from './NewSupportUserThreadModal';

// The staff-facing inbox/messages endpoints enrich the DTOs with fields the base
// types omit: the conversation gets a populated `user` identity + `assignedAdminName`,
// and each message gets the real `senderDisplayName`/`senderAvatar`. Augment locally.
type StaffConversation = UserSupportConversationDTO & {
  user?: { firstName?: string; lastName?: string; avatar?: string };
  assignedAdminName?: string;
};
type StaffMessage = UserSupportMessageDTO & {
  senderDisplayName?: string;
  senderAvatar?: string;
};

const STATUS_LABELS: Record<string, string> = { open: 'Aperto', pending: 'In attesa', resolved: 'Risolto' };
const STATUS_META: Record<string, { label: string; dot: string; active: string }> = {
  open: { label: 'Aperto', dot: 'bg-emerald-500', active: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  pending: { label: 'In attesa', dot: 'bg-amber-500', active: 'bg-amber-50 text-amber-700 ring-amber-200' },
  resolved: { label: 'Risolto', dot: 'bg-neutral-400', active: 'bg-neutral-100 text-neutral-600 ring-neutral-300' },
};
const formatListTime = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, yest)) return 'Ieri';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const FILTERS = [
  { key: undefined, label: 'Tutti' },
  { key: 'open', label: 'Aperti' },
  { key: 'pending', label: 'In attesa' },
  { key: 'resolved', label: 'Risolti' },
] as const;

export function SupportUsersInboxPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [mobileThread, setMobileThread] = useState(false);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [lightbox, setLightbox] = useState<{ src: string; alt?: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openThread = useOpenThread();

  const { data: conversationsData = [], isLoading: listLoading } = useInbox(filter);
  const { data: messagesData, isLoading: msgsLoading } = useSupportMessages(activeId);
  const send = useSendSupportMessage();
  const markRead = useMarkSupportRead();
  const setStatus = useSupportStatus();
  const assign = useSupportAssign();
  const { isTyping, emitTypingStart, emitTypingStop } = useSupportUsersSocket(activeId);

  const conversations = conversationsData as StaffConversation[];

  // Deep-link: ?conversation=<id> apre direttamente quel thread; ?userId=<id>
  // apre (o crea, idempotente) il thread di quel cliente — usato dal bottone
  // "Scrivi all'utente" nella scheda utente admin. Il param viene consumato
  // subito, così un refresh o il "indietro" non riaprono/ricreano nulla.
  const deepConversation = searchParams.get('conversation');
  const deepUserId = searchParams.get('userId');
  useEffect(() => {
    if (!deepConversation && !deepUserId) return;
    const clear = () =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('conversation');
          next.delete('userId');
          return next;
        },
        { replace: true }
      );
    if (deepConversation) {
      setActiveId(deepConversation);
      clear();
      return;
    }
    if (deepUserId && !openThread.isPending) {
      openThread.mutate(deepUserId, {
        onSuccess: (conv) => setActiveId(conv.id),
        onSettled: clear,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepConversation, deepUserId]);

  useEffect(() => {
    if (activeId) setMobileThread(true);
  }, [activeId]);

  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX = 10 * 1024 * 1024;
  const ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
  const [fileError, setFileError] = useState<string | null>(null);
  const onPick = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const bad = picked.find((f) => !ALLOWED.includes(f.type) || f.size > MAX);
    if (bad) {
      setFileError('File non supportato o troppo grande (max 10MB: JPG, PNG, WebP, GIF, PDF)');
      return;
    }
    setFileError(null);
    setFiles((prev) => [...prev, ...picked]);
  };

  useEffect(() => { setFiles([]); setFileError(null); }, [activeId]);

  const active = conversations.find((c) => c.id === activeId);
  const activeUserName = (c: StaffConversation | undefined) =>
    `${c?.user?.firstName ?? ''} ${c?.user?.lastName ?? ''}`.trim() || 'Utente';

  // Mark read on open AND whenever a new user message arrives while the thread is
  // already open (unreadCountStaff goes positive) — re-running on the count, not
  // just on activeId, prevents a stuck badge while actively viewing.
  useEffect(() => {
    if (activeId && active && active.unreadCountStaff > 0) markRead.mutate(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active?.unreadCountStaff]);

  // Side is decided by ROLE: every staff reply (from any admin) belongs on the
  // team side, while the user's messages stay on the other side. Agent name +
  // avatar give the per-admin attribution.
  const messages: BubbleMessage[] = ((messagesData?.data ?? []) as StaffMessage[]).slice().reverse().map((m) => {
    const isStaff = m.senderRole === UserSupportSenderRole.SUPER_ADMIN;
    return {
      id: m.id, content: m.content, status: m.status, createdAt: m.createdAt,
      isMine: isStaff,
      senderName: m.senderDisplayName,
      senderAvatar: m.senderAvatar
        ? resolveUploadUrl(m.senderAvatar)
        : (!isStaff && active?.user?.avatar ? resolveUploadUrl(active.user.avatar) : undefined),
    };
  });

  return (
    <div className="mx-auto flex h-[calc(100vh-5rem)] max-w-7xl flex-col px-4 py-4 sm:px-6 lg:px-8">
      <div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-lg shadow-neutral-200/60">
        {/* List panel */}
        <div className={`flex w-full flex-col border-r border-neutral-200 md:w-[340px] md:shrink-0 ${mobileThread ? 'hidden md:flex' : 'flex'}`}>
          <div className="flex h-[3.75rem] items-center gap-2 border-b border-neutral-200 px-4">
            <h2 className="flex-1 text-sm font-semibold text-neutral-800">Supporto Utenti</h2>
            <button
              type="button"
              onClick={() => setNewThreadOpen(true)}
              className="flex items-center gap-1 rounded-lg bg-brand-orange px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-orange/90"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
              Nuova conversazione
            </button>
          </div>
          {openThread.isError && (
            <p className="border-b border-neutral-200 bg-red-50 px-4 py-2 text-[11px] text-red-600">
              Impossibile aprire la conversazione con questo utente.
            </p>
          )}
          <div className="flex gap-1 border-b border-neutral-200 px-3 py-2">
            {FILTERS.map((f) => (
              <button key={f.label} onClick={() => setFilter(f.key)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${filter === f.key ? 'bg-brand-teal text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {listLoading ? (
              <p className="p-4 text-sm text-neutral-400">Caricamento…</p>
            ) : conversations.length === 0 ? (
              <p className="p-4 text-sm text-neutral-400">{filter ? `Nessun thread ${(STATUS_LABELS[filter] ?? filter).toLowerCase()}` : 'Nessuna richiesta di supporto'}</p>
            ) : conversations.map((c) => (
              <button key={c.id} onClick={() => setActiveId(c.id)}
                className={`flex w-full items-center gap-3 border-b border-neutral-100 px-4 py-3 text-left hover:bg-neutral-50 ${activeId === c.id ? 'bg-brand-teal-50' : ''}`}>
                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-neutral-100">
                  {c.user?.avatar && <img src={resolveUploadUrl(c.user.avatar)} alt="" className="h-9 w-9 object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-neutral-900">
                    {activeUserName(c)}
                  </p>
                  <p className="truncate text-[11px] text-neutral-400">{c.lastMessagePreview ?? '—'}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {c.lastMessageAt && <span className="whitespace-nowrap text-[10px] text-neutral-400">{formatListTime(c.lastMessageAt)}</span>}
                  {c.unreadCountStaff > 0 && <span className="rounded-full bg-brand-orange px-1.5 text-[10px] font-semibold text-white">{c.unreadCountStaff}</span>}
                  <span className="text-[9px] uppercase tracking-wide text-neutral-400">{STATUS_LABELS[c.status]}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Thread panel */}
        <div className={`flex flex-1 flex-col ${mobileThread ? 'flex' : 'hidden md:flex'}`}>
          {active ? (
            <>
              <div className="flex h-16 items-center gap-3 border-b border-neutral-200 bg-white px-4 sm:px-5">
                <button onClick={() => setMobileThread(false)} className="-ml-1 rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100 md:hidden" aria-label="Indietro">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                </button>
                {active.user?.avatar ? (
                  <button
                    type="button"
                    onClick={() => setLightbox({ src: resolveUploadUrl(active.user!.avatar)!, alt: activeUserName(active) })}
                    aria-label="Apri foto profilo"
                    className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-neutral-100 ring-2 ring-neutral-100 transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal"
                  >
                    <img src={resolveUploadUrl(active.user.avatar)} alt="" className="h-9 w-9 object-cover" />
                  </button>
                ) : (
                  <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-neutral-100 ring-2 ring-neutral-100" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-neutral-900">
                    {activeUserName(active)}
                  </p>
                  <p className="flex items-center gap-1.5 text-[11px] text-neutral-400">
                    <span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[active.status]?.dot ?? 'bg-neutral-400'}`} />
                    {active.assignedAdminName ? (
                      <span>Assegnato a <span className="font-medium text-neutral-600">{String(active.assignedAdminId) === String(user?.id) ? 'te' : active.assignedAdminName}</span></span>
                    ) : (
                      <span className="font-medium text-amber-600">Non assegnato</span>
                    )}
                  </p>
                </div>

                {String(active.assignedAdminId) === String(user?.id) ? (
                  <span className="hidden items-center gap-1.5 rounded-lg bg-brand-teal-50 px-2.5 py-1.5 text-[11px] font-medium text-brand-teal ring-1 ring-brand-teal-100 sm:flex">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4 12l5 5L20 6" /></svg>
                    Assegnato a te
                  </span>
                ) : (
                  <button onClick={() => assign.mutate({ conversationId: active.id, assignedAdminId: String(user?.id) })}
                    className="flex items-center gap-1.5 rounded-lg bg-brand-teal-50 px-2.5 py-1.5 text-[11px] font-semibold text-brand-teal ring-1 ring-brand-teal-100 transition-colors hover:bg-brand-teal-100">
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 14a4 4 0 10-8 0M12 7a3 3 0 100-6 3 3 0 000 6zM19 8v6M22 11h-6" /></svg>
                    Assegna a me
                  </button>
                )}

                <div className="hidden items-center gap-0.5 rounded-lg bg-neutral-100 p-0.5 lg:flex">
                  {Object.entries(STATUS_META).map(([k, meta]) => {
                    const on = active.status === k;
                    return (
                      <button key={k} onClick={() => !on && setStatus.mutate({ conversationId: active.id, status: k })}
                        className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-medium transition-all ${on ? `bg-white shadow-sm ring-1 ${meta.active}` : 'text-neutral-500 hover:text-neutral-700'}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>

                <select value={active.status} onChange={(e) => setStatus.mutate({ conversationId: active.id, status: e.target.value })}
                  className="rounded-lg border border-neutral-200 bg-white px-2 py-1.5 text-[11px] font-medium text-neutral-700 lg:hidden">
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>

              <MessageBubbleList
                messages={messages}
                isLoading={msgsLoading}
                emptyTitle="Nessun messaggio"
                onAvatarClick={(src, name) => setLightbox({ src, alt: name })}
                typingLabel={isTyping(active.id) ? "L'utente sta scrivendo…" : null}
                renderAttachments={(id) => {
                  const msg = ((messagesData?.data ?? []) as StaffMessage[]).find((m) => m.id === id);
                  if (!msg || msg.attachments.length === 0) return null;
                  return (
                    <div className="flex flex-col gap-1">
                      {msg.attachments.map((a, i) => (
                        <SupportUserAttachment key={i} messageId={id} index={i} meta={a} />
                      ))}
                    </div>
                  );
                }}
              />

              <MessageComposer
                onSend={(content) => {
                  send.mutate({ conversationId: active.id, content, files });
                  setFiles([]);
                  setFileError(null);
                }}
                onTyping={() => emitTypingStart(active.id)}
                onTypingStop={() => emitTypingStop(active.id)}
                placeholder="Rispondi all'utente…"
                allowEmpty={files.length > 0}
                leftControl={
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => { onPick(e.target.files); e.target.value = ''; }}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Allega file"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-neutral-400 transition-colors hover:bg-neutral-200/70 hover:text-neutral-600"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                      </svg>
                    </button>
                  </>
                }
                topSlot={
                  (files.length > 0 || fileError) ? (
                    <div className="mb-2 flex flex-col gap-1">
                      {fileError && <p className="text-[11px] text-red-600">{fileError}</p>}
                      <div className="flex flex-wrap gap-1">
                        {files.map((f, i) => (
                          <span key={i} className="flex items-center gap-1 rounded-lg bg-neutral-100 px-2 py-1 text-[11px] text-neutral-600">
                            {f.name}
                            <button
                              type="button"
                              onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                              aria-label="Rimuovi"
                              className="text-neutral-400 hover:text-red-500"
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null
                }
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-neutral-400">Seleziona una conversazione</div>
          )}
        </div>
      </div>

      <NewSupportUserThreadModal
        open={newThreadOpen}
        onOpenChange={setNewThreadOpen}
        onCreated={(conversationId) => setActiveId(conversationId)}
      />

      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}
